import React, { useRef, useEffect, useLayoutEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material';
import { Colors } from 'library';

export interface DropdownMenuItem {
    label: string;
    sublabel?: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    to?: string;
    id?: string;
}

interface BaseProps {
    open: boolean;
    onClose: () => void;
    trigger: React.ReactNode;
    header?: string;
    width?: number;
    align?: 'left' | 'right';
}

interface ItemsProps extends BaseProps {
    items: DropdownMenuItem[];
    children?: never;
}

interface ChildrenProps extends BaseProps {
    items?: never;
    children: React.ReactNode;
}

type DropdownMenuProps = ItemsProps | ChildrenProps;

const MENU_GAP = 4;
const VIEWPORT_PADDING = 8;

export function DropdownMenu({
    open,
    onClose,
    items,
    header,
    children,
    trigger,
    width,
    align = 'right',
}: DropdownMenuProps) {
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        const menu = menuRef.current;
        if (!trigger || !menu) return;

        const triggerRect = trigger.getBoundingClientRect();
        const menuHeight = menu.offsetHeight;
        const menuWidth = width ?? menu.offsetWidth;

        const spaceBelow = window.innerHeight - triggerRect.bottom - MENU_GAP;
        const spaceAbove = triggerRect.top - MENU_GAP;
        const openUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;

        let top = openUpward ? triggerRect.top - menuHeight - MENU_GAP : triggerRect.bottom + MENU_GAP;
        let left = align === 'left' ? triggerRect.left : triggerRect.right - menuWidth;

        left = Math.max(VIEWPORT_PADDING, Math.min(left, window.innerWidth - menuWidth - VIEWPORT_PADDING));
        top = Math.max(VIEWPORT_PADDING, Math.min(top, window.innerHeight - menuHeight - VIEWPORT_PADDING));

        setMenuPosition({ top, left });
    }, [align, width]);

    useLayoutEffect(() => {
        if (!open) {
            setMenuPosition(null);
            return;
        }
        updatePosition();
    }, [open, updatePosition, items, children, header]);

    useEffect(() => {
        if (!open) return;
        const handleScrollOrResize = () => updatePosition();
        window.addEventListener('resize', handleScrollOrResize);
        window.addEventListener('scroll', handleScrollOrResize, true);
        return () => {
            window.removeEventListener('resize', handleScrollOrResize);
            window.removeEventListener('scroll', handleScrollOrResize, true);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const inTrigger = triggerRef.current?.contains(target);
            const inMenu = menuRef.current?.contains(target);
            if (!inTrigger && !inMenu && !target.closest('.MuiModal-root')) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open, onClose]);

    const menuContent = children ?? (
        <>
            {header && <MenuHeader>{header}</MenuHeader>}
            {items?.map((item) => {
                const content = (
                    <>
                        {item.icon && <MenuItemIcon>{item.icon}</MenuItemIcon>}
                        <div>
                            {item.label}
                            {item.sublabel && <MenuItemSublabel>{item.sublabel}</MenuItemSublabel>}
                        </div>
                    </>
                );
                if (item.to) {
                    return (
                        <MenuItemLink
                            key={item.label}
                            id={item.id}
                            to={item.to}
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                        >
                            {content}
                            <ChevronRight>&rsaquo;</ChevronRight>
                        </MenuItemLink>
                    );
                }
                return (
                    <MenuItem
                        key={item.label}
                        id={item.id}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            item.onClick?.();
                        }}
                    >
                        {content}
                    </MenuItem>
                );
            })}
        </>
    );

    return (
        <Wrapper ref={triggerRef}>
            {trigger}
            {open &&
                createPortal(
                    <Menu
                        ref={menuRef}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                        }}
                        style={{
                            ...(width ? { width } : undefined),
                            top: menuPosition?.top ?? 0,
                            left: menuPosition?.left ?? 0,
                            visibility: menuPosition ? 'visible' : 'hidden',
                        }}
                    >
                        {menuContent}
                    </Menu>,
                    document.body,
                )}
        </Wrapper>
    );
}

const Wrapper = styled('div')({
    position: 'relative',
});

const Menu = styled('div')({
    position: 'fixed',
    backgroundColor: Colors.white,
    border: `1px solid ${Colors.lightGrey}`,
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    zIndex: 1400,
    overflow: 'hidden',
    minWidth: 160,
});

const MenuHeader = styled('div')({
    fontSize: 11,
    fontWeight: 700,
    color: Colors.mediumGrey,
    letterSpacing: 0.5,
    padding: '8px 16px',
});

const menuItemStyles = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '10px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: 14,
    color: Colors.darkNavy,
    textAlign: 'left' as const,
    textDecoration: 'none',
    '&:hover': {
        backgroundColor: Colors.faintGrey,
    },
    outline: 'none',
    '&:focus, &:focus-visible': { outline: 'none', boxShadow: 'none' },
};

const MenuItem = styled('button')(menuItemStyles);

const MenuItemLink = styled(Link)({
    ...menuItemStyles,
    color: Colors.darkNavy,
    textDecoration: 'none',
    '& > span:last-child': {
        opacity: 0,
        transition: 'opacity 0.15s',
    },
    '&:hover': {
        backgroundColor: Colors.faintGrey,
        color: Colors.darkNavy,
        textDecoration: 'none',
    },
    '&:hover > span:last-child': {
        opacity: 1,
    },
    '&:visited': {
        color: Colors.darkNavy,
    },
});

const ChevronRight = styled('span')({
    marginLeft: 'auto',
    fontSize: 20,
    lineHeight: 1,
    color: Colors.mediumGrey,
});

const MenuItemSublabel = styled('div')({
    fontSize: 12,
    color: Colors.mediumGrey,
    marginTop: 2,
});

const MenuItemIcon = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    marginRight: 8,
    color: Colors.mediumGrey,
});
